import { useState } from "react";
import { useEmail } from "../hooks/useEmail";
import { motion } from "framer-motion";
import {
    faPhone,
    faEnvelope,
    faGlobe,
    faMapMarkerAlt,
    faClock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            ease: "easeOut"
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};
const Contact = () => {
    const { sendEmail, isSending } = useEmail();

    // useState to control form values
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        topic: "",
        message: "",
    });

    // update state on input change
    const handleChange = (e) => {
        const { id, value } = e.target;
        setForm((prev) => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { name, email, phone, city, topic, message } = form;

        const emailMsg = `
        Name: ${name}
        Email: ${email}
        Phone: ${phone}
        City : ${city}
        Topic: ${topic}
        Message: ${message}
      `;

        const subject = `New Contact Form Submission - ${topic}`;
        try {
            await sendEmail(emailMsg, subject);
        } catch (err) {
            console.error(err);
        } finally {
            // ✅ Always reset form
            setForm({
                name: "",
                email: "",
                phone: "",
                city: "",
                topic: "",
                message: "",
            });
        }
    };

    return (
        <div style={{ padding: "155px 0", }}>
            <div className="container " >
                <motion.div
                    className="text-center mb-5"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                >
                    <motion.h4
                        className="fw-bold mb-4 display-5 gradient-text"
                        variants={fadeInUp}
                        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, color: 'var(--primary)' }}
                    >
                        Let's Talk About Your Financial Future
                    </motion.h4>

                    <motion.p
                        className="fs-5 mx-auto lead d-none"
                        variants={fadeInUp}
                        style={{ maxWidth: "800px", fontFamily: "'Inter', sans-serif", lineHeight: 1.7, color: 'var(--text)' }}
                    >
                        At <strong style={{ color: 'var(--primary)' }}>SAG Financials</strong>, every conversation begins with a
                        question: <em style={{ color: 'var(--heading)', fontStyle: 'italic' }}>What matters most to you?</em>
                        Whether you're starting your financial journey, reviewing investments, or seeking expert advice — we're here to
                        help you move forward with clarity and confidence.
                    </motion.p>
                </motion.div>

                {/* Contact Info + Form Side by Side */}
                <motion.div
                    className="row g-5 align-items-start"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                >
                    {/* Contact Info Left */}
                    <motion.div className="col-lg-6 d-flex" variants={itemVariants}>
                        <div className="card flex-fill shadow-lg h-100" style={{ backgroundColor: 'var(--white)', border: '2px solid var(--secondary)' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="fw-bold mb-4 display-6" style={{ fontFamily: "'Playfair Display', serif", color: 'var(--heading)' }}>
                                    Reach Out Today
                                </h3>
                                <p className="mb-4 fs-5" style={{ fontFamily: "'Inter', sans-serif", color: 'var(--text)' }}>
                                    You're just one step away from smarter planning and stronger financial peace of mind.
                                </p>

                                <div className="vstack gap-3">
                                    {[
                                        { icon: faPhone, text: "Call or WhatsApp:", detail: "+91 7011468336", link: "tel:+917011468336" },
                                        { icon: faEnvelope, text: "Email:", detail: "connect@sagfinancials.com", link: "mailto:connect@sagfinancials.com" },
                                        { icon: faGlobe, text: "Website:", detail: "www.sagfinancials.com", link: "https://www.sagfinancials.com" },
                                        { icon: faMapMarkerAlt, text: "Location:", detail: "Gurugram, Haryana ", detail2: "Serving Clients across India & abroad" }
                                    ].map((item, index) => (
                                        <motion.div
                                            key={index}
                                            className="d-flex align-items-start p-3 rounded-3"
                                            style={{ backgroundColor: 'var(--light-bg)' }}
                                            whileHover={{ scale: 1.02 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <FontAwesomeIcon icon={item.icon} className="fs-5 me-3" style={{ color: 'var(--primary)' }} />
                                            <div>
                                                <strong style={{ color: 'var(--heading)' }}>{item.text}</strong>
                                                {item.link ? (
                                                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="ms-1 text-decoration-none contacty fw-bold" style={{ color: 'var(--primary)', }}>
                                                        {item.detail}
                                                    </a>
                                                ) : (
                                                    <span className="ms-1" style={{ color: 'var(--text)' }}>
                                                        {item.detail}
                                                        <br />
                                                        {item.detail2}
                                                    </span>

                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                <motion.div
                                    className="mt-4 p-3 rounded-3 d-flex align-items-center"
                                    style={{ backgroundColor: 'var(--light-bg)' }}

                                >
                                    <FontAwesomeIcon icon={faClock} className="me-3 fs-5" style={{ color: 'var(--primary)' }} />
                                    <div>
                                        <strong style={{ color: 'var(--heading)' }}>Office Hours:</strong>
                                        <div style={{ color: 'var(--secondary)' }}>
                                            <div style={{fontSize:'14px'}}>Mon – Sat : 10:00 A.M. – 6:00 P.M.</div>
                                            <div style={{fontSize:'14px'}}>Sun : 10:00 A.M. – 2:00 P.M.</div>
                                        </div>
                                    </div>
                                </motion.div>

                            </div>
                        </div>
                    </motion.div>

                    {/* Contact Form Right */}
                    <motion.div className="col-lg-6 d-flex" variants={itemVariants}>
                        <div className="card  shadow-lg h-100 flex-fill" style={{ backgroundColor: 'var(--white)', border: '2px solid var(--secondary)' }}>
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="fw-bold mt-3 mb-4" style={{ color: 'var(--heading)' }}>Contact Form</h3>
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        {["name", "email", "phone", "city"].map((field, i) => {
                                            const labelText =
                                                field === "phone"
                                                    ? "Mobile No"
                                                    : field === "city"
                                                        ? "City"
                                                        : field.charAt(0).toUpperCase() + field.slice(1);

                                            return (
                                                <div className="col-md-6" key={i}>
                                                    <div className="form-floating mb-3">
                                                        <input
                                                            type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
                                                            className="form-control"
                                                            id={field}
                                                            placeholder={labelText}
                                                            value={form[field]}
                                                            onChange={handleChange}
                                                            required
                                                            style={{ borderColor: 'var(--primary)' }}
                                                        />
                                                        <label htmlFor={field}>{labelText}</label>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div className="col-12">
                                            <div className="form-floating mb-3" style={{ border: '1px solid var(--primary)' }}>
                                                <select className="form-select" id="topic" value={form.topic} onChange={handleChange} required>
                                                    <option value="">Select Option</option>
                                                    <option>Investments</option>
                                                    <option>Insurance</option>
                                                    <option>Tax Planning</option>
                                                    <option>Loans</option>
                                                    <option>General Enquiry</option>
                                                </select>
                                                <label htmlFor="topic">What do you need help with?</label>
                                            </div>
                                        </div>

                                        <div className="col-12 mb-4">
                                            <div className="form-floating">
                                                <textarea
                                                    className="form-control"
                                                    id="message"
                                                    value={form.message}
                                                    onChange={handleChange}
                                                    style={{ height: "120px", borderColor: 'var(--primary)' }}
                                                    required
                                                ></textarea>
                                                <label htmlFor="message">Your Message</label>
                                            </div>
                                        </div>

                                        <div className="col-12 mt-4">
                                            <button className="btn py-3 px-5 w-100 fw-bold" style={{ backgroundColor: 'var(--primary)', color: 'var(--white)' }}>
                                                Send Message
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>

    );
};

export default Contact;
